import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import StatusBadge from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { generateQuotePdf } from "../lib/pdf";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Download, Eye, Pencil, Trash2, Plus, RefreshCw, ClipboardList, Users, ArrowLeft } from "lucide-react";

const fmt = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function AdminQuotes() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const clientFilter = (params.get("client") || "").toLowerCase();
  const filter = params.get("filter") || (clientFilter ? "all" : "all");

  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [cleanupOpen, setCleanupOpen] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [obQuoteIds, setObQuoteIds] = useState(new Set());
  const [sendingOb, setSendingOb] = useState(null);
  const [refreshing, setRefreshing] = useState(null);
  const [salespersons, setSalespersons] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");

  const toggleOne = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = (ids, on) => setSelected((s) => { const n = new Set(s); ids.forEach((id) => (on ? n.add(id) : n.delete(id))); return n; });

  const bulkDelete = async () => {
    setBulkDeleting(true);
    try {
      const ids = Array.from(selected);
      const { data } = await api.post("/quotes/bulk-delete", { ids });
      setQuotes((qs) => qs.filter((q) => !selected.has(q.id)));
      setSelected(new Set());
      setBulkOpen(false);
      toast.success(`Deleted ${data.deleted} quote${data.deleted === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk delete failed");
    } finally {
      setBulkDeleting(false);
    }
  };

  const setFilter = (f) => {
    const next = new URLSearchParams(params);
    if (f === "all") next.delete("filter"); else next.set("filter", f);
    setParams(next, { replace: true });
  };
  const clearClient = () => {
    const next = new URLSearchParams(params);
    next.delete("client");
    setParams(next, { replace: true });
  };

  const load = () => {
    Promise.all([api.get("/quotes")])
      .then(([q]) => setQuotes(q.data))
      .catch(() => {})
      .finally(() => setLoading(false));
    api.get("/onboarding")
      .then(({ data }) => setObQuoteIds(new Set(data.map((o) => o.quote_id).filter(Boolean))))
      .catch(() => {});
    api.get("/admin/salespersons").then(({ data }) => setSalespersons(data)).catch(() => {});
  };
  useEffect(load, []);

  const assignQuote = async (q, value) => {
    const salesperson_id = value === "none" ? null : value;
    try {
      const { data } = await api.patch(`/quotes/${q.id}/assign`, { salesperson_id });
      setQuotes((qs) => qs.map((x) => (x.id === q.id ? { ...x, assigned_to: data.assigned_to, assigned_to_name: data.assigned_to_name } : x)));
      toast.success(salesperson_id ? `Assigned to ${data.assigned_to_name}` : "Unassigned");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not assign");
    }
  };

  const runCleanup = async () => {
    setCleaning(true);
    try {
      const { data } = await api.post("/admin/cleanup-test-data");
      toast.success(`Removed ${data.deleted} test quote${data.deleted === 1 ? "" : "s"}`);
      setCleanupOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cleanup failed");
    } finally {
      setCleaning(false);
    }
  };

  const sendQuestionnaire = async (q) => {
    if (obQuoteIds.has(q.id)) { navigate("/admin/onboarding"); return; }
    setSendingOb(q.id);
    try {
      await api.post("/onboarding", { client_name: q.client_name, client_email: q.client_email, quote_id: q.id });
      setObQuoteIds((s) => new Set(s).add(q.id));
      toast.success(`Questionnaire sent to ${q.client_email}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send questionnaire");
    } finally {
      setSendingOb(null);
    }
  };

  const confirmDelete = async () => {
    const id = deleteTarget.id;
    setDeleteTarget(null);
    try {
      await api.delete(`/quotes/${id}`);
      setQuotes((qs) => qs.filter((q) => q.id !== id));
      toast.success("Quote deleted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    }
  };

  const refreshQuote = async (q) => {
    setRefreshing(q.id);
    try {
      await api.post(`/quotes/${q.id}/refresh`);
      toast.success(`Quote refreshed — a fresh 10-day link is on its way to ${q.client_email}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not refresh quote");
    } finally {
      setRefreshing(null);
    }
  };

  const DAY = 86400000;
  const expiryOf = (q) => q.expires_at ? new Date(q.expires_at) : (q.created_at ? new Date(new Date(q.created_at).getTime() + 10 * DAY) : null);
  const isActiveQ = (q) => !q.signed && !q.deposit_paid;
  const isExpiredQ = (q) => { if (!isActiveQ(q)) return false; const e = expiryOf(q); return !!e && e.getTime() < Date.now(); };
  const isExpiringSoonQ = (q) => { if (!isActiveQ(q) || !q.public_token) return false; const e = expiryOf(q); if (!e) return false; const t = e.getTime(); return t >= Date.now() && t <= Date.now() + 3 * DAY; };

  const scoped = clientFilter ? quotes.filter((q) => (q.client_email || "").toLowerCase() === clientFilter) : quotes;
  const leadCount = scoped.filter((q) => q.source === "public").length;
  const tabFiltered =
    filter === "leads" ? scoped.filter((q) => q.source === "public")
    : filter === "expiring" ? scoped.filter(isExpiringSoonQ)
    : filter === "expired" ? scoped.filter(isExpiredQ)
    : scoped;
  const visibleQuotes = statusFilter === "all" ? tabFiltered : tabFiltered.filter((q) => q.status === statusFilter);

  const clientName = clientFilter ? (scoped[0]?.client_name || clientFilter) : null;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="admin-quotes">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <button onClick={() => navigate("/admin")} className="font-mono text-xs uppercase tracking-[0.2em] text-primary flex items-center gap-1 hover:underline" data-testid="back-to-overview">
            <ArrowLeft className="h-3 w-3" /> Admin Console
          </button>
          <h1 className="mt-2 font-display font-black text-4xl tracking-tighter">Quotes</h1>
          {clientFilter && (
            <p className="mt-1 font-mono text-xs text-zinc-500" data-testid="client-scope">
              Filtered by client: <span className="text-zinc-900 font-medium">{clientName}</span>
              <button onClick={clearClient} data-testid="clear-client-filter" className="ml-2 text-primary hover:underline">✕ clear</button>
            </p>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={() => navigate("/admin/clients")} data-testid="quotes-to-clients-btn" variant="outline" className="rounded-none border border-zinc-900 bg-blue-50 text-blue-700 hover:bg-blue-100 hover:text-blue-800 font-mono uppercase tracking-widest text-xs">
            <Users className="h-4 w-4 mr-1" /> Clients
          </Button>
          <Button onClick={() => navigate("/admin/users")} data-testid="quotes-to-users-btn" variant="outline" className="rounded-none border border-zinc-900 bg-green-50 text-green-700 hover:bg-green-100 hover:text-green-800 font-mono uppercase tracking-widest text-xs">
            <Users className="h-4 w-4 mr-1" /> Users
          </Button>
          <Button onClick={() => setCleanupOpen(true)} data-testid="clear-test-data-btn" variant="outline" className="rounded-none border border-zinc-900 bg-red-50 text-red-700 hover:bg-red-100 hover:text-red-800 font-mono uppercase tracking-widest text-xs">
            <Trash2 className="h-4 w-4 mr-1" /> Clear Test Data
          </Button>
          <Button onClick={() => navigate("/build")} data-testid="admin-add-quote-btn" className="rounded-none border border-zinc-900 bg-primary hover:bg-[#5E1F92] text-white font-mono uppercase tracking-widest">
            <Plus className="h-4 w-4 mr-1" /> Add Quote
          </Button>
        </div>
      </div>

      {loading ? (
        <p className="font-mono text-sm text-zinc-500">Loading…</p>
      ) : (
        <>
        <div className="flex items-center justify-between gap-3 mb-4 border-b border-zinc-200 flex-wrap">
          <div className="flex gap-1 flex-wrap">
            <button data-testid="filter-all-tab" onClick={() => setFilter("all")} className={`px-4 py-2 font-mono text-xs uppercase tracking-widest border-b-2 -mb-px transition-colors ${filter === "all" ? "border-primary text-primary" : "border-transparent text-zinc-500 hover:text-zinc-900"}`}>
              All Quotes ({scoped.length})
            </button>
            <button data-testid="filter-leads-tab" onClick={() => setFilter("leads")} className={`px-4 py-2 font-mono text-xs uppercase tracking-widest border-b-2 -mb-px transition-colors ${filter === "leads" ? "border-primary text-primary" : "border-transparent text-zinc-500 hover:text-zinc-900"}`}>
              Leads ({leadCount})
            </button>
            <button data-testid="filter-expiring-tab" onClick={() => setFilter("expiring")} className={`px-4 py-2 font-mono text-xs uppercase tracking-widest border-b-2 -mb-px transition-colors ${filter === "expiring" ? "border-primary text-primary" : "border-transparent text-zinc-500 hover:text-zinc-900"}`}>
              Expiring ({scoped.filter(isExpiringSoonQ).length})
            </button>
            <button data-testid="filter-expired-tab" onClick={() => setFilter("expired")} className={`px-4 py-2 font-mono text-xs uppercase tracking-widest border-b-2 -mb-px transition-colors ${filter === "expired" ? "border-primary text-primary" : "border-transparent text-zinc-500 hover:text-zinc-900"}`}>
              Expired ({scoped.filter(isExpiredQ).length})
            </button>
          </div>
          <div className="flex items-center gap-2 pb-2">
            <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">Status</span>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="status-filter-select" className="w-[140px] rounded-none border-zinc-300 font-mono text-xs h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="accepted">Accepted</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        {selected.size > 0 && (
          <div className="flex items-center justify-between gap-3 mb-3 px-4 py-2.5 bg-primary/5 border border-primary/20" data-testid="quotes-bulk-bar">
            <span className="font-mono text-xs uppercase tracking-widest text-primary">{selected.size} selected</span>
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())} className="rounded-none font-mono text-xs uppercase tracking-widest">Clear</Button>
              <Button size="sm" data-testid="quotes-bulk-delete-btn" onClick={() => setBulkOpen(true)} className="rounded-none bg-red-600 hover:bg-red-700 text-white font-mono text-xs uppercase tracking-widest">
                <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete selected
              </Button>
            </div>
          </div>
        )}
        <div className="border border-zinc-200 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-200 font-mono text-xs uppercase tracking-widest text-zinc-500">
                <th className="px-4 py-3 w-10">
                  <Checkbox
                    data-testid="quotes-select-all"
                    checked={visibleQuotes.length > 0 && visibleQuotes.every((q) => selected.has(q.id))}
                    onCheckedChange={(v) => toggleAll(visibleQuotes.map((q) => q.id), !!v)}
                  />
                </th>
                <th className="text-left px-4 py-3">Quote</th>
                <th className="text-left px-4 py-3">Client</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Assigned</th>
                <th className="text-right px-4 py-3">One-time</th>
                <th className="text-right px-4 py-3">Monthly</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleQuotes.map((q) => (
                <tr key={q.id} data-testid={`admin-quote-row-${q.id}`} className="border-b border-zinc-100 hover:bg-zinc-50">
                  <td className="px-4 py-3">
                    <Checkbox data-testid={`quote-select-${q.id}`} checked={selected.has(q.id)} onCheckedChange={() => toggleOne(q.id)} />
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => navigate(`/quotes/${q.id}`)} className="font-medium hover:text-primary">
                      #{q.id.slice(-8).toUpperCase()}
                    </button>
                    {q.source === "public" && (
                      <span data-testid={`lead-badge-${q.id}`} className="ml-2 font-mono text-[9px] uppercase tracking-wider bg-primary/10 text-primary px-1.5 py-0.5">Lead</span>
                    )}
                    {q.from_audit && (
                      <span data-testid={`audit-badge-${q.id}`} className="ml-2 font-mono text-[9px] uppercase tracking-wider bg-amber-100 text-amber-700 px-1.5 py-0.5">Audit</span>
                    )}
                    <p className="text-xs text-zinc-400 font-mono">{new Date(q.created_at).toLocaleDateString()}</p>
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-medium">{q.client_name}</p>
                    <p className="text-xs text-zinc-500">{q.client_email}</p>
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={q.status} /></td>
                  <td className="px-4 py-3">
                    <Select value={q.assigned_to || "none"} onValueChange={(v) => assignQuote(q, v)}>
                      <SelectTrigger data-testid={`assign-select-${q.id}`} className="w-[150px] rounded-none border-zinc-300 font-mono text-xs h-8">
                        <SelectValue placeholder="Unassigned" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Unassigned</SelectItem>
                        {salespersons.map((sp) => (
                          <SelectItem key={sp.id} value={sp.id}>{sp.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-4 py-3 text-right font-mono">{fmt(q.one_time_total)}</td>
                  <td className="px-4 py-3 text-right font-mono text-zinc-500">{q.monthly_total ? `${fmt(q.monthly_total)}/mo` : "—"}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1 flex-wrap">
                      {(isExpiredQ(q) || isExpiringSoonQ(q)) && (
                        <Button size="sm" variant="ghost" data-testid={`admin-refresh-${q.id}`} disabled={refreshing === q.id} onClick={() => refreshQuote(q)} className="rounded-none h-8 px-2 text-primary hover:bg-primary/10" title="Refresh — extend 10 days & re-email link">
                          <RefreshCw className={`h-3.5 w-3.5 ${refreshing === q.id ? "animate-spin" : ""}`} />
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" data-testid={`admin-view-${q.id}`} onClick={() => navigate(`/quotes/${q.id}`)} className="rounded-none h-8 px-2" title="View">
                        <Eye className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" data-testid={`admin-edit-${q.id}`} disabled={q.deposit_paid || q.signed} onClick={() => navigate(`/build?edit=${q.id}`)} className="rounded-none h-8 px-2 disabled:opacity-40" title={(q.deposit_paid || q.signed) ? "Locked — signed or paid" : "Edit"}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" data-testid={`admin-delete-${q.id}`} disabled={q.deposit_paid || q.signed} onClick={() => setDeleteTarget(q)} className="rounded-none h-8 px-2 text-red-600 hover:text-red-700 hover:bg-red-50 disabled:opacity-40 disabled:text-zinc-400" title={(q.deposit_paid || q.signed) ? "Locked — signed or paid" : "Delete"}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" data-testid={`admin-questionnaire-${q.id}`} disabled={sendingOb === q.id} onClick={() => sendQuestionnaire(q)} className={`rounded-none h-8 px-2 ${obQuoteIds.has(q.id) ? "text-green-600 hover:bg-green-50" : "text-primary hover:bg-primary/10"}`} title={obQuoteIds.has(q.id) ? "Questionnaire sent — view responses" : "Send onboarding questionnaire"}>
                        <ClipboardList className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="outline" data-testid={`admin-download-${q.id}`} onClick={() => generateQuotePdf(q)} className="rounded-none h-8 border-zinc-300 px-2" title="Download PDF">
                        <Download className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {visibleQuotes.length === 0 && <p className="p-10 text-center text-zinc-500">{statusFilter !== "all" ? `No ${statusFilter} quotes in this view.` : filter === "leads" ? "No self-serve leads yet." : filter === "expiring" ? "No quotes expiring in the next 3 days." : filter === "expired" ? "No expired open quotes." : "No quotes yet."}</p>}
        </div>
        </>
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this quote?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget && `Quote #${deleteTarget.id.slice(-8).toUpperCase()} for ${deleteTarget.client_name} will be permanently removed.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-btn" onClick={confirmDelete} className="rounded-none bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={bulkOpen} onOpenChange={(o) => !o && setBulkOpen(false)}>
        <AlertDialogContent className="rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selected.size} quote{selected.size === 1 ? "" : "s"}?</AlertDialogTitle>
            <AlertDialogDescription>
              The selected quotes (including any that are signed or paid) will be permanently removed, along with their payment records. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-bulk-delete-btn" disabled={bulkDeleting} onClick={(e) => { e.preventDefault(); bulkDelete(); }} className="rounded-none bg-red-600 hover:bg-red-700">
              {bulkDeleting ? "Deleting…" : `Delete ${selected.size}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={cleanupOpen} onOpenChange={(o) => !o && setCleanupOpen(false)}>
        <AlertDialogContent className="rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle>Clear test data?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes synthetic test quotes (emails on example.com / acme.com, "anon@test.com", and any client name containing "test") and resets the public rate-limit log. Your real client quotes are not affected. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-cleanup-btn" disabled={cleaning} onClick={(e) => { e.preventDefault(); runCleanup(); }} className="rounded-none bg-red-600 hover:bg-red-700">
              {cleaning ? "Clearing…" : "Clear test data"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
